/**
 * Operator detail page (refreshed).
 *
 * Hero card + tabs (Overview / Tenant grants / Actions). Behaviour
 * preserved: rename / role flip / activate / rotate-password / revoke-totp
 * / grant / revoke — all super-only, all server-gated. Confirm dialog
 * and TempPasswordModal keep their bespoke ``role="alertdialog"`` markup
 * because they're deliberately non-dismissable.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  KeyRound,
  Power,
  PowerOff,
  ShieldCheck,
  ShieldOff,
  Trash2,
  User,
  X,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SelectNative } from "@/components/ui/select-native";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { TempPasswordModal } from "@/components/shell/TempPasswordModal";
import {
  asApiError,
  getOperator,
  getTenants,
  grantOperatorTenant,
  patchOperator,
  revokeOperatorTenant,
  revokeOperatorTotp,
  rotateOperatorPassword,
  type AdminRole,
  type OperatorRow,
  type RotatePasswordResponse,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

function operatorInitials(email: string, displayName: string): string {
  if (displayName) {
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (local.slice(0, 2) || "??").toUpperCase();
}

export function OperatorDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["admin-user", id] as const,
    queryFn: () => getOperator(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load this operator", {
        description: "They may not exist or you may not have access.",
      });
    }
  }, [query.error]);

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div>
            <CardTitle className="text-base">Couldn't load this operator</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              They may not exist or you may not have access.{" "}
              <Link to="/operators" className="text-foreground underline">
                Back to operators
              </Link>
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <OperatorDetailLoaded
      key={query.data.id}
      operator={query.data}
      isSuper={user?.role === "super"}
      currentUserId={user?.id ?? null}
      onChanged={async () => {
        await qc.invalidateQueries({ queryKey: ["admin-user", id] });
        await qc.invalidateQueries({ queryKey: ["admin-users"] });
      }}
      navigateBack={() => navigate("/operators")}
    />
  );
}

function OperatorDetailLoaded({
  operator,
  isSuper,
  currentUserId,
  onChanged,
  navigateBack: _navigateBack,
}: {
  operator: OperatorRow;
  isSuper: boolean;
  currentUserId: string | null;
  onChanged: () => Promise<void>;
  navigateBack: () => void;
}) {
  const [displayName, setDisplayName] = useState(operator.display_name);
  const [rotateResponse, setRotateResponse] =
    useState<RotatePasswordResponse | null>(null);
  const [confirmTotpRevoke, setConfirmTotpRevoke] = useState(false);
  const [rotateRequestedEmail, setRotateRequestedEmail] = useState(true);

  // Tenants list for the "grant tenant" select. Cheap fetch; the server
  // filters out tenants the caller cannot see.
  const tenantsQuery = useQuery({
    queryKey: ["tenants", { for: "operator-grant" }] as const,
    queryFn: () => getTenants({ page_size: 200 }),
  });

  const isSelf = currentUserId !== null && currentUserId === operator.id;
  const canMutate = isSuper && !isSelf;

  const patchMut = useMutation({
    mutationFn: (body: { display_name?: string; role?: AdminRole; is_active?: boolean }) =>
      patchOperator(operator.id, body),
    onSuccess: async () => {
      toast.success("Operator updated");
      await onChanged();
    },
    onError: (err) => toastError(err),
  });

  const rotateMut = useMutation({
    mutationFn: (body: { send_email: boolean }) =>
      rotateOperatorPassword(operator.id, body),
    onSuccess: async (resp) => {
      setRotateResponse(resp);
      await onChanged();
    },
    onError: (err) => toastError(err),
  });

  const revokeTotpMut = useMutation({
    mutationFn: () => revokeOperatorTotp(operator.id),
    onSuccess: async () => {
      toast.success("TOTP revoked");
      setConfirmTotpRevoke(false);
      await onChanged();
    },
    onError: (err) => toastError(err),
  });

  const grantMut = useMutation({
    mutationFn: (tenantId: string) => grantOperatorTenant(operator.id, tenantId),
    onSuccess: async () => {
      toast.success("Tenant grant added");
      await onChanged();
    },
    onError: (err) => toastError(err),
  });

  const revokeGrantMut = useMutation({
    mutationFn: (tenantId: string) => revokeOperatorTenant(operator.id, tenantId),
    onSuccess: async () => {
      toast.success("Tenant grant revoked");
      await onChanged();
    },
    onError: (err) => toastError(err),
  });

  function onRename(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!isSuper) return;
    if (displayName === operator.display_name) return;
    patchMut.mutate({ display_name: displayName });
  }

  function onToggleActive() {
    if (!canMutate) return;
    patchMut.mutate({ is_active: !operator.is_active });
  }

  function onFlipRole() {
    if (!canMutate) return;
    const next: AdminRole = operator.role === "super" ? "tenant" : "super";
    patchMut.mutate({ role: next });
  }

  function onRotatePassword(sendEmail: boolean) {
    if (!canMutate) return;
    setRotateRequestedEmail(sendEmail);
    rotateMut.mutate({ send_email: sendEmail });
  }

  const grantedIds = new Set(operator.tenants.map((t) => t.id));
  const grantCandidates =
    tenantsQuery.data?.results.filter((t) => !grantedIds.has(t.id)) ?? [];

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 h-auto px-2 text-muted-foreground">
        <Link to="/operators">
          <ArrowLeft /> Back to operators
        </Link>
      </Button>

      <Card className="mb-6 brand-edge p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
                {operatorInitials(operator.email, operator.display_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight">{operator.email}</h1>
                <Badge variant={operator.role === "super" ? "default" : "muted"} className="capitalize">
                  {operator.role === "super" ? (
                    <ShieldCheck className="h-3 w-3" />
                  ) : (
                    <User className="h-3 w-3" />
                  )}
                  {operator.role}
                </Badge>
                <Badge variant={operator.is_active ? "success" : "muted"}>
                  {operator.is_active ? "active" : "inactive"}
                </Badge>
                {operator.totp_enrolled ? (
                  <Badge variant="success">TOTP enrolled</Badge>
                ) : (
                  <Badge variant="warning">TOTP not enrolled</Badge>
                )}
                {isSelf ? <Badge variant="info">You</Badge> : null}
              </div>
              {operator.display_name ? (
                <p className="text-sm text-muted-foreground">{operator.display_name}</p>
              ) : null}
              <code className="block truncate font-mono text-xs text-muted-foreground/80">
                {operator.id}
              </code>
            </div>
          </div>
        </div>
      </Card>

      {isSelf ? (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-info/40 bg-info/5 p-3 text-sm text-info">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            You're viewing your own operator row. Privileged actions
            (rotate password, revoke TOTP, flip role, deactivate) are
            disabled here — ask another super-admin.
          </p>
        </div>
      ) : null}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="grants">
            Tenant grants
            <span className="ml-1.5 text-xs text-muted-foreground">
              {operator.role === "super" ? "all" : operator.tenants.length}
            </span>
          </TabsTrigger>
          {canMutate ? <TabsTrigger value="actions">Actions</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Identity</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Email is immutable. Display name is operator-visible.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="mt-1 font-mono text-sm">{operator.email}</p>
                </div>
                <form className="space-y-2" onSubmit={onRename} noValidate>
                  <Label htmlFor="display_name">Display name</Label>
                  <div className="flex gap-2">
                    <Input
                      id="display_name"
                      value={displayName}
                      maxLength={120}
                      onChange={(e) => setDisplayName(e.target.value)}
                      disabled={!isSuper || patchMut.isPending}
                    />
                    <Button
                      type="submit"
                      variant="outline"
                      disabled={
                        !isSuper ||
                        patchMut.isPending ||
                        displayName === operator.display_name
                      }
                    >
                      Save
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A super may rename themselves. Other identity edits require
                    another super-admin.
                  </p>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Role & status</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Super sees every tenant. Tenant operators are scoped to grants.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Role</Label>
                    <p className="mt-1 text-sm font-medium capitalize">{operator.role}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <p className="mt-1 text-sm font-medium">
                      {operator.is_active ? "active" : "inactive"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">TOTP</Label>
                    <p className="mt-1 text-sm font-medium">
                      {operator.totp_enrolled ? "enrolled" : "not enrolled"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Last login</Label>
                    <p className="mt-1 text-sm font-medium">
                      {operator.last_login_at
                        ? new Date(operator.last_login_at).toLocaleString()
                        : "—"}
                    </p>
                  </div>
                </div>
                {canMutate ? (
                  <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onFlipRole}
                      disabled={patchMut.isPending}
                      data-testid="operator-flip-role"
                    >
                      {operator.role === "super" ? <User /> : <ShieldCheck />}
                      {operator.role === "super" ? "Demote to tenant" : "Promote to super"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onToggleActive}
                      disabled={patchMut.isPending}
                      data-testid="operator-toggle-active"
                    >
                      {operator.is_active ? <PowerOff /> : <Power />}
                      {operator.is_active ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                ) : null}
                <p className="text-xs text-muted-foreground">
                  Promoting to super drops every tenant grant. Demoting to
                  tenant requires re-granting tenants before they can act.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="grants">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tenant grants</CardTitle>
              <p className="text-xs text-muted-foreground">
                Tenants this operator can see and act on. Super operators see
                every tenant; no grants are stored for them.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {operator.role === "super" ? (
                <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
                  Super operators see all tenants. No grants are tracked.
                </div>
              ) : operator.tenants.length === 0 ? (
                <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
                  No tenants granted. This operator cannot see any tenant data
                  until at least one grant is added.
                </div>
              ) : (
                <ul className="divide-y rounded-md border bg-card">
                  {operator.tenants.map((t) => (
                    <li
                      key={t.id}
                      className="flex items-center justify-between gap-2 p-3"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="bg-primary/10 text-[10px] text-primary">
                            {t.name.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate font-medium">{t.name}</span>
                        <Badge
                          variant={t.environment === "live" ? "success" : "warning"}
                          className="capitalize"
                        >
                          {t.environment}
                        </Badge>
                      </div>
                      {isSuper ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeGrantMut.mutate(t.id)}
                          disabled={revokeGrantMut.isPending}
                          data-testid={`operator-revoke-grant-${t.id}`}
                          aria-label={`Revoke ${t.name}`}
                        >
                          <X /> Revoke
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}

              {isSuper && operator.role === "tenant" && grantCandidates.length > 0 ? (
                <form
                  className="flex flex-col gap-2 sm:flex-row"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget);
                    const tid = String(fd.get("tenant_id") ?? "");
                    if (tid) grantMut.mutate(tid);
                  }}
                >
                  <SelectNative
                    name="tenant_id"
                    disabled={grantMut.isPending}
                    defaultValue=""
                    data-testid="operator-grant-select"
                    className="flex-1"
                  >
                    <option value="" disabled>
                      Add a tenant grant…
                    </option>
                    {grantCandidates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.environment})
                      </option>
                    ))}
                  </SelectNative>
                  <Button type="submit" disabled={grantMut.isPending}>
                    Grant
                  </Button>
                </form>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        {canMutate ? (
          <TabsContent value="actions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Password reset</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Rotating revokes the operator's sessions and mints a one-shot
                  temporary password.
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  onClick={() => onRotatePassword(true)}
                  disabled={rotateMut.isPending}
                  data-testid="operator-rotate-password"
                >
                  <KeyRound />
                  {rotateMut.isPending ? "Rotating…" : "Rotate password (+ email)"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onRotatePassword(false)}
                  disabled={rotateMut.isPending}
                >
                  <KeyRound />
                  Rotate password (no email)
                </Button>
              </CardContent>
            </Card>

            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle className="text-base text-destructive">Danger zone</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Revoking TOTP wipes the operator's authenticator + recovery
                  codes; they're forced to re-enroll on next login.
                </p>
              </CardHeader>
              <CardContent>
                <Button
                  variant="destructive"
                  onClick={() => setConfirmTotpRevoke(true)}
                  disabled={revokeTotpMut.isPending || !operator.totp_enrolled}
                  data-testid="operator-revoke-totp"
                >
                  <ShieldOff />
                  Revoke TOTP
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}
      </Tabs>

      {rotateResponse ? (
        <TempPasswordModal
          title="Password rotated — share this temporary password"
          emailDispatched={rotateResponse.email_dispatched}
          sendEmailRequested={rotateRequestedEmail}
          targetEmail={operator.email}
          tempPassword={rotateResponse.temp_password}
          onDone={() => setRotateResponse(null)}
        />
      ) : null}

      {confirmTotpRevoke ? (
        <ConfirmDialog
          title="Revoke TOTP enrollment?"
          message={`This will clear ${operator.email}'s TOTP secret + recovery codes and revoke their sessions. They'll be forced to re-enroll on next login.`}
          confirmLabel={revokeTotpMut.isPending ? "Revoking…" : "Revoke TOTP"}
          onCancel={() => setConfirmTotpRevoke(false)}
          onConfirm={() => revokeTotpMut.mutate()}
        />
      ) : null}
    </>
  );
}

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <Trash2 className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold">{title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{message}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function toastError(err: unknown) {
  const body = asApiError(err);
  if (body?.error === "cannot_modify_self") {
    toast.error("You can't perform that action on yourself", {
      description: "Ask another super-admin.",
    });
  } else if (body?.error === "last_super_admin") {
    toast.error("This action would leave zero active super-admins", {
      description: "Promote or activate another super first.",
    });
  } else if (body?.error === "duplicate_grant") {
    toast.error("This operator already has a grant on that tenant.");
  } else if (body?.error === "role_mismatch") {
    toast.error("Super operators don't take tenant grants", {
      description: "Demote them first if needed.",
    });
  } else if (body) {
    toast.error(body.detail ?? `Action failed (${body.error}).`);
  } else {
    toast.error("Action failed", { description: "Check your network and try again." });
  }
}

export default OperatorDetailPage;
